import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { passwordAPI, userAPI } from "../lib/api";
import { Lock, Key, UserX, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export function PasswordManager({ open, onOpenChange }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Change Password State
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  // Reset Password State (Admin only)
  const [resetPasswordData, setResetPasswordData] = useState({
    username: "",
    newTempPassword: ""
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    if (changePasswordData.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    try {
      setLoading(true);
      await passwordAPI.changePassword(changePasswordData.currentPassword, changePasswordData.newPassword);
      toast.success("Password changed successfully");
      
      // Reset form
      setChangePasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetPasswordData.username.trim()) {
      toast.error("Username is required");
      return;
    }

    try {
      setLoading(true);
      const response = await passwordAPI.resetPassword(resetPasswordData.username);
      
      setResetPasswordData({
        username: resetPasswordData.username,
        newTempPassword: response.data.temporary_password
      });
      
      toast.success(`Password reset successfully for ${response.data.username}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Password Management
          </DialogTitle>
          <DialogDescription>
            Change your password or reset passwords for other users
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="change-password" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="change-password">Change Password</TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger value="reset-password">Reset User Password</TabsTrigger>
            )}
          </TabsList>

          {/* Change Password Tab */}
          <TabsContent value="change-password" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Change Your Password
                </CardTitle>
                <CardDescription>
                  Enter your current password and choose a new one
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <Label htmlFor="current-password">Current Password *</Label>
                    <div className="relative">
                      <Input
                        id="current-password"
                        type={showPasswords.current ? "text" : "password"}
                        value={changePasswordData.currentPassword}
                        onChange={(e) => setChangePasswordData({ 
                          ...changePasswordData, 
                          currentPassword: e.target.value 
                        })}
                        placeholder="Enter your current password"
                        required
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => togglePasswordVisibility('current')}
                      >
                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="new-password">New Password *</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPasswords.new ? "text" : "password"}
                        value={changePasswordData.newPassword}
                        onChange={(e) => setChangePasswordData({ 
                          ...changePasswordData, 
                          newPassword: e.target.value 
                        })}
                        placeholder="Enter new password (min 6 characters)"
                        required
                        minLength={6}
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => togglePasswordVisibility('new')}
                      >
                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirm-password">Confirm New Password *</Label>
                    <div className="relative">
                      <Input
                        id="confirm-password"
                        type={showPasswords.confirm ? "text" : "password"}
                        value={changePasswordData.confirmPassword}
                        onChange={(e) => setChangePasswordData({ 
                          ...changePasswordData, 
                          confirmPassword: e.target.value 
                        })}
                        placeholder="Confirm new password"
                        required
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => togglePasswordVisibility('confirm')}
                      >
                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button type="submit" disabled={loading}>
                      {loading ? "Changing..." : "Change Password"}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reset Password Tab (Admin only) */}
          {user?.role === 'admin' && (
            <TabsContent value="reset-password" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserX className="w-4 h-4" />
                    Reset User Password
                  </CardTitle>
                  <CardDescription>
                    Generate a new temporary password for any user
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div>
                      <Label htmlFor="reset-username">Username *</Label>
                      <Input
                        id="reset-username"
                        type="text"
                        value={resetPasswordData.username}
                        onChange={(e) => setResetPasswordData({ 
                          ...resetPasswordData, 
                          username: e.target.value,
                          newTempPassword: "" // Clear temp password when username changes
                        })}
                        placeholder="Enter username to reset"
                        required
                      />
                    </div>

                    {resetPasswordData.newTempPassword && (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Key className="w-4 h-4 text-yellow-600" />
                          <span className="font-medium text-yellow-800">New Temporary Password</span>
                        </div>
                        <div className="font-mono text-lg bg-white p-2 rounded border">
                          {resetPasswordData.newTempPassword}
                        </div>
                        <p className="text-xs text-yellow-700 mt-2">
                          Please provide this temporary password to the user. They should change it immediately after logging in.
                        </p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button type="submit" disabled={loading}>
                        {loading ? "Resetting..." : "Reset Password"}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}